import React, { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Input,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { Link } from 'react-router-dom';
import type { MpProduct } from '../../../api/MpProducts';
import * as MpProductsApi from '../../../api/MpProducts';
import type { MpLinkRow } from '../useGoodsDetail';

const { Link: AntLink, Text } = Typography;

const externalUrl = (p: MpProduct): string | null => {
  if (!p.external_id) return null;
  if (p.marketplace === 'wildberries') {
    return `https://www.wildberries.ru/catalog/${p.external_id}/detail.aspx`;
  }
  if (p.marketplace === 'ozon' || p.marketplace === 'ozon_fresh') {
    return `https://www.ozon.ru/product/${p.external_id}`;
  }
  return null;
};

type Props = {
  article: string;
  rows: MpLinkRow[];
  pending?: boolean;
  onChanged: () => void;
  openAuthId?: number | null;
  onPickerClosed?: () => void;
};

export const MarketplacesTab = ({
  article,
  rows,
  pending,
  onChanged,
  openAuthId,
  onPickerClosed,
}: Props) => {
  const [pickerAuthId, setPickerAuthId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [candidates, setCandidates] = useState<MpProduct[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const openPicker = (authId: number) => {
    setPickerAuthId(authId);
    setSearch('');
    setCandidates([]);
    setSelectedId(null);
  };

  useEffect(() => {
    if (openAuthId != null) openPicker(openAuthId);
  }, [openAuthId]);

  const loadCandidates = async (authId: number, q: string) => {
    setSearch(q);
    const res = await MpProductsApi.list({
      marketplace_auth_id: authId,
      linked: 'unlinked',
      archived: 'all',
      search: q.trim() || undefined,
      limit: 50,
    });
    setCandidates(res.items);
  };

  useEffect(() => {
    if (pickerAuthId == null) return;
    void loadCandidates(pickerAuthId, '');
  }, [pickerAuthId]);

  const closePicker = () => {
    setPickerAuthId(null);
    onPickerClosed?.();
  };

  const saveLink = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      await MpProductsApi.link(selectedId, article);
      message.success('Связь сохранена');
      closePicker();
      onChanged();
    } catch (e: any) {
      message.error(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const unlink = (product: MpProduct) => {
    Modal.confirm({
      title: 'Отвязать товар МП?',
      content: `${product.marketplace}: ${product.seller_sku}`,
      okText: 'Отвязать',
      okButtonProps: { danger: true },
      onOk: async () => {
        await MpProductsApi.unlink(product.id);
        message.success('Связь удалена');
        onChanged();
      },
    });
  };

  return (
    <>
      <Table
        size="middle"
        loading={pending}
        pagination={false}
        rowKey={(r) => r.auth.id}
        dataSource={rows}
        columns={[
          {
            title: 'Интеграция',
            render: (_, r) => (
              <Link
                to={`/settings/auth_marketplace/${encodeURIComponent(
                  r.auth.marketplace,
                )}`}
              >
                <Text strong>
                  {r.auth.marketplace} #{r.auth.id}
                </Text>
              </Link>
            ),
          },
          {
            title: 'Карточка МП',
            render: (_, r) => {
              if (!r.product) {
                return <Badge status="warning" text="Не связана" />;
              }
              const url = externalUrl(r.product);
              return url ? (
                <AntLink href={url} target="_blank" rel="noreferrer">
                  {r.product.external_id}
                </AntLink>
              ) : (
                r.product.external_id
              );
            },
          },
          {
            title: 'Seller SKU',
            render: (_, r) => r.product?.seller_sku || '—',
          },
          {
            title: 'Название / бренд',
            render: (_, r) =>
              r.product ? (
                <span>
                  {r.product.title || '—'}
                  {r.product.brand ? (
                    <Text type="secondary"> · {r.product.brand}</Text>
                  ) : null}
                </span>
              ) : (
                '—'
              ),
          },
          {
            title: 'Связь',
            width: 140,
            render: (_, r) => {
              if (!r.product)
                return <Badge status="warning" text="Требуется связь" />;
              return <Badge status="success" text="Связана" />;
            },
          },
          {
            title: 'На МП',
            width: 150,
            render: (_, r) => {
              if (!r.product) return '—';
              return r.product.is_archived ? (
                <Tag>Удалённый</Tag>
              ) : (
                <Tag color="success">Активный</Tag>
              );
            },
          },
          {
            title: 'Синхронизация',
            width: 160,
            render: (_, r) =>
              r.product?.synced_at
                ? new Date(r.product.synced_at).toLocaleString('ru')
                : '—',
          },
          {
            title: 'Действия',
            width: 200,
            render: (_, r) => (
              <Space wrap>
                {r.product ? (
                  <>
                    <Button size="small" onClick={() => openPicker(r.auth.id)}>
                      Заменить
                    </Button>
                    <Button
                      size="small"
                      danger
                      onClick={() => unlink(r.product!)}
                    >
                      Отвязать
                    </Button>
                  </>
                ) : (
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => openPicker(r.auth.id)}
                  >
                    Привязать
                  </Button>
                )}
              </Space>
            ),
          },
        ]}
      />

      <Modal
        title="Выбор товара МП"
        open={pickerAuthId != null}
        onCancel={closePicker}
        onOk={saveLink}
        okText="Сохранить"
        confirmLoading={saving}
        okButtonProps={{ disabled: !selectedId }}
        width={760}
        destroyOnClose
      >
        <Input.Search
          placeholder="Поиск sku / barcode / title"
          value={search}
          onChange={(e) =>
            pickerAuthId != null &&
            void loadCandidates(pickerAuthId, e.target.value)
          }
          style={{ marginBottom: 12 }}
          allowClear
        />
        <Table
          size="small"
          rowKey="id"
          pagination={{ pageSize: 8 }}
          dataSource={candidates}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: selectedId != null ? [selectedId] : [],
            onChange: (keys) => setSelectedId(Number(keys[0]) || null),
          }}
          onRow={(record) => ({
            onClick: () => setSelectedId(record.id),
          })}
          columns={[
            { title: 'sku', dataIndex: 'seller_sku' },
            { title: 'barcode', dataIndex: 'barcode', width: 120 },
            {
              title: 'На МП',
              width: 120,
              render: (_, p: MpProduct) =>
                p.is_archived ? (
                  <Tag>Удалённый</Tag>
                ) : (
                  <Tag color="success">Активный</Tag>
                ),
            },
            { title: 'бренд', dataIndex: 'brand', width: 120 },
            { title: 'название', dataIndex: 'title' },
          ]}
          rowClassName={(p) => (p.is_archived ? 'ant-table-row-disabled' : '')}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          Удалённый — товар пропал из каталога МП при последнем sync. Лучше
          привязывать активные карточки.
        </Text>
      </Modal>
    </>
  );
};
